<template>
  <Toast />
  <ConfirmDialog />

  <div :class="[layout + '-layout layout']">
    <!-- Navbar section -->
    <div class="navbar">
      <Navbar />
    </div>

    <!-- Sidebar section -->
    <div v-if="layout == 'admin'" class="sidebar">
      <Sidebar />
    </div>

    <!-- Content section -->
    <div class="content">
      <router-view />
    </div>

    <Footer v-if="layout == 'web'" ref="footerRef" />
  </div>
</template>

<script setup>
import { onMounted, onBeforeMount, computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { useRoute } from "vue-router";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";

import Navbar from "@/components/panels/Navbar.vue";
import Sidebar from "@/components/panels/Sidebar.vue";
import Footer from "@/components/panels/Footer.vue";
import { calculateBodyMinHeight } from "@/composables/_commons/helper.js";

// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const route = useRoute();
const store = useStore();
const layout = computed(() => store.getters.layout);
const toast = useToast();
const footerRef = ref(null);
// #endregion

// #region Functions
function handleCalculateMinHeight() {
  calculateBodyMinHeight().then(() => {
    if (footerRef.value) {
      footerRef.value.showFooter();
    }
  });
}

function changeLayout() {
  const layout = localStorage.getItem("layout");
  store.commit("setLayout", layout);
}
// #endregion

// #region Effects, Hooks
onBeforeMount(() => {
  const preloader = document.getElementById("preloader");

  if (preloader) {
    preloader.remove();
  }
});

onMounted(async () => {
  changeLayout();

  localStorage.removeItem("errorToastMessage");
  document.body.classList.add("app-mounted");

  const host = window.location.host;

  if (host.includes("localhost")) {
    store.commit("setDialogsCloseOnEscape", true);
    store.commit("setDialogsDismissableMask", true);
  }

  handleCalculateMinHeight();

  window.addEventListener("resize", () => {
    handleCalculateMinHeight();
  });

  setInterval(() => {
    const errorToastMessage = localStorage.getItem("errorToastMessage");

    if (errorToastMessage) {
      toast.add({
        severity: "error",
        summary: "Se ha producido un error",
        detail: errorToastMessage,
        life: 5000,
      });

      localStorage.removeItem("errorToastMessage");
    }
  });
});

watch(route, () => {
  changeLayout();
});
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
@import "@/styles/_vars.scss";

.layout {
  display: grid;
  grid-template-columns: 15% auto;
  grid-template-rows: min-content auto;
  height: 100vh;
  overflow-x: hidden;
  grid-template-areas:
    "navbar navbar"
    "sidebar content";

  .navbar {
    grid-area: navbar;
  }

  .sidebar {
    grid-area: sidebar;
    margin: 0.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.25);
    overflow: hidden;
  }

  .content {
    grid-area: content;
    overflow-y: auto;
  }

  &.admin-layout {
    .navbar {
      :deep(.navbar-section) {
        background-color: $dark-primary;
      }
    }
  }

  &.web-layout {
    display: unset;

    .navbar {
      position: sticky;
      top: 0;
      z-index: $navbar-z-index;
    }

    .content {
      min-height: var(--header-footer-height);
    }
  }
}

@media (min-width: $mobile-min-width) and (max-width: $mobile-max-width) {
  .layout {
    grid-template-columns: 100%;
    grid-template-areas:
      "navbar"
      "content";
  }
}

@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .layout {
    grid-template-columns: 25% auto;
  }
}
</style>
